/**
 * seoFromProto — map a backend `seo.v1.SeoData` to the frontend `SeoMeta`
 * (ADR 0142). One place owns the proto→SeoMeta translation for every dynamic
 * detail page (listings / blog / estates), so the og:type / og:locale / JSON-LD
 * fields land consistently and the mapping can't drift across loaders.
 */
import type { DescMessage } from '@bufbuild/protobuf';
import { assertNever } from '@letsgethome/lib/utils/enums';
import { canonicalFromWire, type CanonicalUrl } from '@letsgethome/lib/weburl';
import { Locale as ProtoLocale } from '@letsgethome/proto/common/v1/locale_pb';
import { OgType } from '@letsgethome/proto/seo/v1/og_type_pb';
import { RobotsDirective } from '@letsgethome/proto/seo/v1/robots_directive_pb';
import type { SeoData } from '@letsgethome/proto/seo/v1/seo_pb';
import { plainText, type Locale, type LocalizedText } from '@letsgethome/types/branded';

import type { OgLocaleTag, OgTypeString, RobotsPolicy, SeoMeta } from '../../types/seo';
import { resourceRobots, undeterminedRobots } from './robots';

/** BCP-47 locale tag → `og:locale` form (`en`, `zh-HK` → `zh_HK`). The cast
 * is sound by construction: the runtime hyphen→underscore replace is exactly
 * the type-level transform that derives [`OgLocaleTag`] from [`Locale`]. */
export function ogLocale(locale: Locale): OgLocaleTag {
  return locale.replace('-', '_') as OgLocaleTag;
}

function ogTypeString(t: OgType): OgTypeString {
  switch (t) {
    case OgType.ARTICLE:
      return 'article';
    case OgType.PRODUCT:
      return 'product';
    case OgType.PROFILE:
      return 'profile';
    default:
      return 'website';
  }
}

/**
 * Map the closed `seo.v1.RobotsDirective` wire enum onto the web-side
 * [`RobotsPolicy`] union. Exhaustive with a `never` guard, so adding a new
 * `RobotsDirective` variant to the proto SSOT is a compile error here until a
 * web rendering decision is made (ADR 0142 META-10). The `noindex,follow` arm
 * is the one the prior boolean collapse lost — it keeps faceted/paginated index
 * pages crawlable (links followed) while de-indexing the duplicate URL.
 * `UNSPECIFIED` defaults to `index`: the backend always sets a concrete
 * directive for indexable resources (enforced by the coverage lints), so an
 * unset value is treated as the indexable default rather than silently hiding a
 * real page.
 */
function robotsPolicyFromProto(r: RobotsDirective): RobotsPolicy {
  switch (r) {
    case RobotsDirective.NOINDEX_FOLLOW:
      return 'noindex-follow';
    case RobotsDirective.NOINDEX_NOFOLLOW:
      return 'noindex-nofollow';
    case RobotsDirective.INDEX_FOLLOW:
    case RobotsDirective.UNSPECIFIED:
      return 'index';
    default:
      return assertNever(r);
  }
}

function protoLocaleToOg(l: ProtoLocale): OgLocaleTag | undefined {
  switch (l) {
    case ProtoLocale.EN:
      return 'en';
    case ProtoLocale.ZH_HK:
      return 'zh_HK';
    case ProtoLocale.ZH_CN:
      return 'zh_CN';
    default:
      return undefined;
  }
}

const LOCALE_CODE: Record<ProtoLocale, Locale | ''> = {
  [ProtoLocale.UNSPECIFIED]: '',
  [ProtoLocale.EN]: 'en',
  [ProtoLocale.ZH_HK]: 'zh-HK',
  [ProtoLocale.ZH_CN]: 'zh-CN',
};

/**
 * Translate a backend `SeoData` into `SeoMeta`. `fallbackTitle` is used when the
 * backend didn't compute a page title (defensive — backend always sets one for
 * indexable resources). `pageTitle` / `metaDescription` / `ogImageAlt` are
 * backend-resolved DATA, so they mint `LocalizedText` via `plainText`.
 *
 * `carrier` is the generated descriptor of the proto message that DECLARES the
 * `seo` field being read (`ListPartnersResponseSchema`, `PartnerSchema`, …). It
 * exists for exactly one reason: when the field is absent, the degradation
 * signal has to name it. Taking a DESCRIPTOR rather than a string means the
 * name is derived from the proto SSOT — it cannot be typo'd, and it follows a
 * message rename instead of going stale. It is REQUIRED so that "report the
 * degradation" is not a step a new loader can forget.
 *
 * Suffix contract (SSOT-W1): a backend-built `pageTitle` is FINAL — the
 * truncate-then-brand door already fit the brand inside the title budget — so
 * this mapper declares `noSuffix` EXPLICITLY. SeoHead no longer sniffs
 * `title.includes(SITE_NAME)` to guess; only the defensive fallback-title path
 * (no backend title) leaves the suffix to SeoHead.
 */
export function seoFromProto(
  seo: SeoData | undefined,
  fallbackTitle: LocalizedText,
  carrier: DescMessage
): SeoMeta & { noSuffix?: boolean } {
  if (!seo) {
    // FAIL CLOSED, LOUDLY. The backend did not compute SeoData for this render —
    // an RPC that returned none, or a defensive fallback path. It sets a
    // concrete SeoData for every indexable resource (enforced by the coverage
    // lints), so reaching here means this render's indexability is UNKNOWN:
    // serving it as an indexable, canonical-less, near-empty 200 is exactly the
    // soft-duplicate/"phantom page" class this mapper must not create.
    //
    // The POLICY is unchanged (`noindex, nofollow` — fail closed is the right
    // instinct, ADR 0142 / V9). What changed is that the VALUE is no longer the
    // same one a deliberate noindex produces. It used to be, and that is why
    // `/partners` — `indexable: true` in the manifest since #1513 — spent a
    // quarter flipping noindex⇄indexable across Ahrefs crawls with nobody able
    // to see it: any `ListPartners` degradation silently de-indexed the whole
    // partner directory in all three locales, then self-healed. An
    // `undetermined` decision names the missing field, and `createLoader` turns
    // it into a level-50 log naming the route.
    return {
      title: fallbackTitle,
      robots: undeterminedRobots(`${carrier.typeName}.seo`),
    };
  }
  // Canonical + hreflang are ADOPTED, never re-composed: `canonicalFromWire`
  // takes the proto `Url` message, so this mapper cannot assemble an origin and
  // a path of its own — the value it hands on is the one the Rust URL core
  // minted through `locale_path` (#4711).
  const alternates: Partial<Record<Locale, CanonicalUrl>> = {};
  for (const alt of seo.alternates) {
    const code = LOCALE_CODE[alt.locale];
    const url = canonicalFromWire(alt.url);
    if (code && url) alternates[code] = url;
  }
  return {
    noSuffix: Boolean(seo.pageTitle),
    title: seo.pageTitle ? plainText(seo.pageTitle) : fallbackTitle,
    description: seo.metaDescription ? plainText(seo.metaDescription) : undefined,
    canonical: canonicalFromWire(seo.canonicalUrl),
    ogImage: seo.ogImage?.value || undefined,
    ogImageAlt: seo.ogImageAlt ? plainText(seo.ogImageAlt) : undefined,
    ogImageWidth: seo.ogImageWidth || undefined,
    ogImageHeight: seo.ogImageHeight || undefined,
    robots: resourceRobots(robotsPolicyFromProto(seo.robots)),
    ogType: ogTypeString(seo.ogType),
    locale: protoLocaleToOg(seo.ogLocale),
    structuredData: seo.structuredData,
    alternates: Object.keys(alternates).length ? alternates : undefined,
    // Same source as the BreadcrumbList JSON-LD (SeoData.breadcrumbs) → the
    // visible <Breadcrumbs> and the structured data can't drift. Labels are
    // backend-resolved data, so they mint LocalizedText via plainText.
    breadcrumbs: seo.breadcrumbs.length
      ? seo.breadcrumbs.map((b) => ({ label: plainText(b.label), url: b.url?.value ?? '' }))
      : undefined,
  };
}
